/**
 * Renders task results as plain text meant for a person to read — pasted into
 * an email, a memo, or a draft — rather than the raw JSON the API returns.
 */

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** "reference_numerals" / "referenceNumerals" -> "Reference Numerals" */
export function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b[a-z]/g, c => c.toUpperCase());
}

const scalarToText = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v);

/** True when a value fits on the same line as its label. */
const isInlineable = (v: unknown): boolean =>
  !isPlainObject(v) && (!Array.isArray(v) || v.every(item => !isPlainObject(item) && !Array.isArray(item)));

function inlineText(v: unknown): string {
  if (Array.isArray(v)) return v.map(scalarToText).filter(Boolean).join(', ');
  return scalarToText(v);
}

function renderValue(value: unknown, depth: number): string[] {
  const pad = '  '.repeat(depth);

  if (Array.isArray(value)) {
    const lines: string[] = [];
    for (const item of value) {
      if (isInlineable(item)) {
        const t = inlineText(item);
        if (t) lines.push(`${pad}• ${t}`);
      } else {
        const sub = renderValue(item, depth + 1);
        if (sub.length) {
          lines.push(`${pad}•${sub[0].slice(pad.length + 1)}`);
          lines.push(...sub.slice(1));
        }
      }
    }
    return lines;
  }

  if (isPlainObject(value)) {
    const lines: string[] = [];
    for (const [k, v] of Object.entries(value)) {
      if (v === null || v === undefined || v === '') continue;
      const label = humanizeKey(k);
      if (isInlineable(v)) {
        const t = inlineText(v);
        if (t) lines.push(`${pad}${label}: ${t}`);
      } else {
        lines.push(`${pad}${label}:`);
        lines.push(...renderValue(v, depth + 1));
      }
    }
    return lines;
  }

  const t = scalarToText(value);
  return t ? [`${pad}${t}`] : [];
}

/** Parts list: one line per part, "Name: numerals", with any extra detail indented. */
function formatPartsList(data: Record<string, unknown>): string {
  const lines: string[] = ['Parts List', ''];

  for (const [key, raw] of Object.entries(data)) {
    const entry = isPlainObject(raw) ? raw : {};
    const pick = (...names: string[]) => {
      for (const n of names) {
        const hit = entry[n] ?? entry[n.toLowerCase()] ?? entry[humanizeKey(n).replace(/ /g, '_')];
        if (hit !== undefined && hit !== null && hit !== '') return hit;
      }
      return undefined;
    };

    const numerals = pick('reference_numerals', 'Reference_Numerals');
    const nums = Array.isArray(numerals) ? numerals.map(scalarToText).join(', ') : scalarToText(numerals);
    lines.push(nums ? `${humanizeKey(key)}: ${nums}` : humanizeKey(key));

    for (const field of ['description', 'details'] as const) {
      const v = pick(field);
      if (v) lines.push(`  ${scalarToText(v)}`);
    }

    const citations = pick('citations');
    if (citations) {
      const c = Array.isArray(citations) ? citations.map(scalarToText).join(', ') : scalarToText(citations);
      if (c) lines.push(`  Citations: ${c}`);
    }
  }

  return lines.join('\n');
}

/** Heuristic matching PartsListDisplay's own detection. */
function looksLikePartsList(data: Record<string, unknown>): boolean {
  const entries = Object.values(data);
  if (entries.length === 0) return false;
  return entries.every(
    e => isPlainObject(e) && ('reference_numerals' in e || 'Reference_Numerals' in e),
  );
}

/**
 * Convert a parsed task result into human-readable text.
 * Strings pass through untouched — they are already prose.
 */
export function formatResultForCopy(result: unknown, taskType?: string): string {
  if (typeof result === 'string') return result;
  if (result === null || result === undefined) return '';

  if (isPlainObject(result) && (taskType === 'parts_list' || looksLikePartsList(result))) {
    return formatPartsList(result);
  }

  const lines = renderValue(result, 0);
  // Nothing structured to show (e.g. a number or an empty object) — fall back to JSON.
  return lines.length ? lines.join('\n') : JSON.stringify(result, null, 2);
}
